import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccessTime,
  DeleteOutlined,
  GpsFixed,
  Public,
  Tune,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  addMaterialGrade,
  commitConfig,
  loadSiteConfigFromAPI,
  setAvailableGrades as setAvailableGradesAction,
  setSiteOperationalInputs,
  type DumpingPoint,
  type GradeApiItem,
  type LoadingPoint,
  type MaterialGrade,
} from 'store/site-config.slice';
import FleetPlanningHandler from 'handlers/fleet-planning.handler';
import { setFleetLevelInput } from 'store/fleet-planning.slice';
import { type SiteConfigPayload } from 'api/fleet-planning.api';
import CustomerConfigAPI from 'api/customer-config.api';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { useSiteConfigUnsavedGuard } from './hooks/useSiteConfigUnsavedGuard';
import useAppTheme from 'hooks/useAppTheme';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

// ── API types ──────────────────────────────────────────────────────────────

interface LocationApiItem {
  locationId: string;
  name: string;
  locationType: 'loading' | 'dumping' | 'intersection';
  active: boolean;
  minutes: number;
  geohashes: string[];
  centroidLat: number;
  centroidLon: number;
}

// ── Internal helpers ───────────────────────────────────────────────────────

const TEMP_PREFIX = '__new__';
type LocalLoadingPoint = LoadingPoint & { _isNew?: boolean };
type LocalDumpingPoint = DumpingPoint & { _isNew?: boolean };

let tempCounter = 0;
function newTempId(prefix: 'L' | 'D') {
  return `${TEMP_PREFIX}${prefix}${++tempCounter}`;
}

// ── Component ──────────────────────────────────────────────────────────────

export const SiteConfigView = ({
  onSaveSuccess,
}: {
  onSaveSuccess?: () => void;
} = {}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  useSiteConfigUnsavedGuard();

  // ── Redux ────────────────────────────────────────────────────────────────
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const customerCode = useAppSelector((s) => s.persistedReducer.customer.code);
  const reduxLoadingPoints = useAppSelector(
    (s) => s.siteConfigReducer.loadingPoints
  );
  const reduxDumpingPoints = useAppSelector(
    (s) => s.siteConfigReducer.dumpingPoints
  );
  const reduxTravelTimes = useAppSelector(
    (s) => s.siteConfigReducer.travelTimes
  );
  const reduxMaterialGrades = useAppSelector(
    (s) => s.siteConfigReducer.materialGrades
  );
  const reduxFleetLevelInput = useAppSelector(
    (s) => s.fleetPlanningReducer.fleetLevelInput
  );
  const reduxSiteOperational = useAppSelector((s) => ({
    productionTarget: s.siteConfigReducer.productionTarget,
    excessIdling: s.siteConfigReducer.excessIdling,
    lunchTolerance: s.siteConfigReducer.lunchTolerance,
  }));
  const fleetPlanningHandler = new FleetPlanningHandler();

  // ── Form state ───────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);
  const [minimalFleetError, setMinimalFleetError] = useState<string | null>(
    null
  );

  const [loadingPoints, setLoadingPoints] = useState<LocalLoadingPoint[]>([]);
  const [dumpingPoints, setDumpingPoints] = useState<LocalDumpingPoint[]>([]);
  const [travelTimes, setTravelTimes] = useState<
    Record<string, { loaded: string; empty: string }>
  >({});
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [materialGrades, setMaterialGrades] = useState<MaterialGrade[]>([]);
  const [saveAttempted, setSaveAttempted] = useState(false);

  // Extra UI fields
  const [productionTarget, setProductionTarget] = useState<string>('0');
  const [excessIdling, setExcessIdling] = useState<string>('0');
  const [lunchTolerance, setLunchTolerance] = useState<string>('0');
  const [dumpingTargetGradeStrings, setDumpingTargetGradeStrings] = useState<
    Record<string, string>
  >({});
  // Tracks raw string values for number-typed point fields so clearing works
  const [rawInputs, setRawInputs] = useState<Record<string, string>>({});

  const sanitize = (raw: string) =>
    raw === '' ? '' : raw.replace(/^0+(\d)/, '$1');

  // ── Seed form from persisted Redux state on mount ─────────────────────────
  useEffect(() => {
    if (reduxLoadingPoints.length > 0) {
      setLoadingPoints(reduxLoadingPoints.map((lp) => ({ ...lp })));
    }
    if (reduxDumpingPoints.length > 0) {
      setDumpingPoints(reduxDumpingPoints.map((dp) => ({ ...dp })));
      const dts: Record<string, string> = {};
      reduxDumpingPoints.forEach((dp) => {
        dts[dp.id] = dp.targetBlend != null ? String(dp.targetBlend) : '';
      });
      setDumpingTargetGradeStrings(dts);
    }
    if (reduxTravelTimes.length > 0) {
      const tt: Record<string, { loaded: string; empty: string }> = {};
      reduxTravelTimes.forEach((t) => {
        tt[`${t.loadingSiteId}-${t.dumpingSiteId}`] = {
          loaded: String(t.loadedMins),
          empty: String(t.emptyMins),
        };
      });
      setTravelTimes(tt);
    }
    if (reduxMaterialGrades.length > 0) {
      setMaterialGrades(reduxMaterialGrades);
    }
    if (reduxSiteOperational.productionTarget > 0) {
      setProductionTarget(String(reduxSiteOperational.productionTarget));
    }
    const savedExcessIdling =
      reduxSiteOperational.excessIdling !== '0'
        ? reduxSiteOperational.excessIdling
        : reduxFleetLevelInput.excessIdling;
    if (savedExcessIdling) setExcessIdling(savedExcessIdling);
    const savedLunchTolerance =
      reduxSiteOperational.lunchTolerance !== '0'
        ? reduxSiteOperational.lunchTolerance
        : reduxFleetLevelInput.lunchTolerance;
    if (savedLunchTolerance) setLunchTolerance(savedLunchTolerance);
  }, []); // eslint-disable-line

  // ── Location / Grade API state ───────────────────────────────────────────
  const [availableLocations, setAvailableLocations] = useState<
    LocationApiItem[]
  >([]);
  const [availableGrades, setAvailableGrades] = useState<GradeApiItem[]>([]);
  const [primaryElement, setPrimaryElement] = useState<string>('CU');
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // Dropdown anchor elements for "Add" menus
  const [addLoadingMenuAnchor, setAddLoadingMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [addDumpingMenuAnchor, setAddDumpingMenuAnchor] =
    useState<HTMLElement | null>(null);
  // ── Fetch locations + grades + customer config once ──────────────────────
  useEffect(() => {
    if (!accessToken || !customerId || !customerCode) return;
    const baseUrl = process.env.REACT_APP_MANAGEMENT_API_URL ?? '';
    const headers = { Authorization: `Bearer ${accessToken}` };
    setIsLoadingLocations(true);
    Promise.all([
      fetch(`${baseUrl}/api/location-importer?customerId=${customerId}`, {
        headers,
      }).then(async (r) => await (r.json() as Promise<LocationApiItem[]>)),
      fetch(`${baseUrl}/api/grade-importer?customerId=${customerId}`, {
        headers,
      }).then(async (r) => await (r.json() as Promise<GradeApiItem[]>)),
      CustomerConfigAPI.getCustomerConfig(customerCode).catch(() => null),
    ])
      .then(([locations, grades, customerConfig]) => {
        setAvailableLocations(locations);
        setAvailableGrades(grades);
        dispatch(setAvailableGradesAction(grades));
        if (customerConfig?.primaryElement) {
          setPrimaryElement(customerConfig.primaryElement.toUpperCase());
        }
      })
      .catch((err) => {
        console.error('Failed to fetch location/grade data', err);
      })
      .finally(() => {
        setIsLoadingLocations(false);
      });
  }, [accessToken, customerId, customerCode]);

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaveAttempted(true);
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    setSaveAttempted(false);
    setMinimalFleetError(null);
    setIsSaving(true);

    try {
      const existingLpNums = loadingPoints
        .filter((lp) => !lp._isNew)
        .map((lp) => parseInt(lp.id.replace(/^L/, ''), 10))
        .filter((n) => !isNaN(n));
      let nextLpNum =
        existingLpNums.length > 0 ? Math.max(...existingLpNums) + 1 : 0;

      const existingDpNums = dumpingPoints
        .filter((dp) => !dp._isNew)
        .map((dp) => parseInt(dp.id.replace(/^D/, ''), 10))
        .filter((n) => !isNaN(n));
      let nextDpNum =
        existingDpNums.length > 0 ? Math.max(...existingDpNums) + 1 : 0;

      const lpIdMap: Record<string, string> = {};
      const dpIdMap: Record<string, string> = {};

      const finalLoadingPoints: Array<Omit<LoadingPoint, '_isNew'>> =
        loadingPoints.map(({ _isNew, ...lp }) => {
          const enriched = { ...lp };
          if (!enriched.gradeId && availableGrades.length > 0) {
            const grade = availableGrades.find(
              (g) => g.locationName === lp.name
            );
            if (grade) {
              enriched.gradeId = grade.gradeId;
              enriched.gradeLocationId = grade.locationId;
              enriched.materialGradeValue =
                grade.qualities?.[primaryElement] != null
                  ? Number(grade.qualities[primaryElement])
                  : undefined;
            }
          }
          if (_isNew) {
            const realId = `L${nextLpNum++}`;
            lpIdMap[lp.id] = realId;
            return { ...enriched, id: realId };
          }
          lpIdMap[lp.id] = lp.id;
          return enriched;
        });

      const finalDumpingPoints: Array<Omit<DumpingPoint, '_isNew'>> =
        dumpingPoints.map(({ _isNew, ...dp }) => {
          if (_isNew) {
            const realId = `D${nextDpNum++}`;
            dpIdMap[dp.id] = realId;
            const targetBlend =
              parseFloat(dumpingTargetGradeStrings[dp.id] ?? '0') || 0;
            return { ...dp, id: realId, blendRatios: [], targetBlend };
          }
          dpIdMap[dp.id] = dp.id;
          const targetBlend =
            parseFloat(dumpingTargetGradeStrings[dp.id] ?? '0') || 0;
          return { ...dp, blendRatios: [], targetBlend };
        });

      const finalTravelTimes = Object.entries(travelTimes)
        .filter(([key]) => !key.includes(TEMP_PREFIX))
        .map(([key, { loaded, empty }]) => {
          const dashIdx = key.indexOf('-');
          return {
            loadingSiteId:
              lpIdMap[key.slice(0, dashIdx)] ?? key.slice(0, dashIdx),
            dumpingSiteId:
              dpIdMap[key.slice(dashIdx + 1)] ?? key.slice(dashIdx + 1),
            loadedMins: Number(loaded) || 0,
            emptyMins: Number(empty) || 0,
          };
        });

      const payload: SiteConfigPayload = {
        loadingPoints: finalLoadingPoints,
        dumpingPoints: finalDumpingPoints,
        travelTimes: finalTravelTimes,
        materialGrades,
      };

      dispatch(loadSiteConfigFromAPI(payload));
      dispatch(setFleetLevelInput({ excessIdling, lunchTolerance }));
      dispatch(
        setSiteOperationalInputs({
          productionTarget: Number(productionTarget),
          excessIdling,
          lunchTolerance,
        })
      );

      // Build minimal fleet count payload from saved points
      const grades = finalLoadingPoints
        .filter((lp) => lp.gradeId)
        .map((lp) => ({
          gradeId: lp.gradeId as string,
          materialGrade: lp.materialGradeValue ?? 0,
        }));
      const dumpingSitesPayload = finalDumpingPoints
        .filter((dp) => dp.locationId)
        .map((dp) => ({
          locationId: dp.locationId as string,
          targetBlend: (dp.targetBlend ?? 0) / 100,
        }));
      if (grades.length > 0 && dumpingSitesPayload.length > 0 && customerId) {
        await fleetPlanningHandler.getMinimalFleetCount({
          customerId,
          grades,
          dumpingSites: dumpingSitesPayload,
          shiftNumber: 1,
          targetDeliveredTons: Number(productionTarget),
          targetBlendTolerance: 0.05,
        });
      }

      // Only commit config and navigate once API succeeds
      dispatch(commitConfig());
      onSaveSuccess?.();
    } catch (_) {
      setMinimalFleetError(t('cdp_minimal_fleet_count_error'));
    } finally {
      setIsSaving(false);
    }
  };

  // ── Point mutations ───────────────────────────────────────────────────────
  const updateLoadingPoint = (
    id: string,
    field: keyof LocalLoadingPoint,
    value: unknown
  ) => {
    setLoadingPoints((prev) =>
      prev.map((lp) => (lp.id === id ? { ...lp, [field]: value } : lp))
    );
  };

  const updateDumpingPoint = (
    id: string,
    field: keyof LocalDumpingPoint,
    value: unknown
  ) => {
    setDumpingPoints((prev) =>
      prev.map((dp) => (dp.id === id ? { ...dp, [field]: value } : dp))
    );
  };

  const handleDeleteLoadingPoint = (id: string) => {
    setLoadingPoints((prev) => prev.filter((lp) => lp.id !== id));
    setTravelTimes((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => !key.startsWith(id + '-'))
      )
    );
  };

  const handleDeleteDumpingPoint = (id: string) => {
    setDumpingPoints((prev) => prev.filter((dp) => dp.id !== id));
    setDumpingTargetGradeStrings((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
    setTravelTimes((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => !key.endsWith('-' + id))
      )
    );
  };

  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!productionTarget || Number(productionTarget) <= 0)
      errors.productionTarget = t(
        'site_config_production_target_must_be_positive'
      );

    if (loadingPoints.length === 0)
      errors.loadingPoints = t(
        'site_config_at_least_one_loading_site_required'
      );

    if (dumpingPoints.length === 0)
      errors.dumpingPoints = t(
        'site_config_at_least_one_dumping_site_required'
      );

    if (
      excessIdling === '' ||
      excessIdling === null ||
      excessIdling === undefined
    )
      errors.excessIdling = t('site_config_idling_tolerance_required');

    if (
      lunchTolerance === '' ||
      lunchTolerance === null ||
      lunchTolerance === undefined
    )
      errors.lunchTolerance = t('site_config_lunch_tolerance_required');

    loadingPoints.forEach((lp) => {
      if (!lp.name.trim())
        errors[`lp-${lp.id}-name`] = t('site_config_name_required');
      if (lp.loadingCapacity <= 0)
        errors[`lp-${lp.id}-capacity`] = t(
          'site_config_must_be_greater_than_0'
        );
      if (lp.loadingDurationMins <= 0)
        errors[`lp-${lp.id}-duration`] = t(
          'site_config_must_be_greater_than_0'
        );
    });
    dumpingPoints.forEach((dp) => {
      if (!dp.name.trim())
        errors[`dp-${dp.id}-name`] = t('site_config_name_required');
      if (dp.dumpingCapacity <= 0)
        errors[`dp-${dp.id}-capacity`] = t(
          'site_config_must_be_greater_than_0'
        );
      if (dp.dumpingDurationMins <= 0)
        errors[`dp-${dp.id}-duration`] = t(
          'site_config_must_be_greater_than_0'
        );
      const blendStr = dumpingTargetGradeStrings[dp.id];
      if (blendStr === '' || blendStr === undefined)
        errors[`dp-${dp.id}-targetBlend`] = t('required');
    });
    return errors;
  };

  // ── Add from API dropdown helpers ─────────────────────────────────────────

  const ensureGradeInList = (grade: GradeApiItem): MaterialGrade => {
    const existing = materialGrades.find((mg) => mg.value === grade.name);
    if (existing) return existing;
    const newGrade: MaterialGrade = {
      value: grade.name,
      category: grade.materialCategory?.toUpperCase() ?? '',
    };
    setMaterialGrades((prev) => [...prev, newGrade]);
    dispatch(addMaterialGrade(newGrade));
    return newGrade;
  };

  const handleAddLoadingFromMenu = (grade: GradeApiItem) => {
    ensureGradeInList(grade);
    // Look up the matching location from location importer for duration/active/geohash
    const location = availableLocations.find(
      (l) => l.name === grade.locationName
    );
    const newLp: LocalLoadingPoint = {
      id: newTempId('L'),
      name: grade.locationName,
      isActive: location?.active ?? true,
      isSelected: true,
      loadingDurationMins: location?.minutes ?? 0,
      materialGrade: grade.name,
      loadingCapacity: grade.inventory ?? 0,
      location: location?.geohashes[0] ?? '',
      gradeId: grade.gradeId,
      gradeLocationId: grade.locationId,
      materialGradeValue:
        grade.qualities?.[primaryElement] != null
          ? Number(grade.qualities[primaryElement])
          : undefined,
      _isNew: true,
    };
    setLoadingPoints((prev) => [...prev, newLp]);
    setAddLoadingMenuAnchor(null);
  };

  const handleAddDumpingFromMenu = (location: LocationApiItem) => {
    const newId = newTempId('D');
    const newDp: LocalDumpingPoint = {
      id: newId,
      name: location.name,
      isActive: location.active,
      isSelected: true,
      dumpingDurationMins: location.minutes,
      blendRatios: [],
      dumpingCapacity: 0,
      location: location.geohashes[0] ?? '',
      locationId: location.locationId,
      _isNew: true,
    };
    setDumpingPoints((prev) => [...prev, newDp]);
    setDumpingTargetGradeStrings((prev) => ({ ...prev, [newId]: '' }));
    setAddDumpingMenuAnchor(null);
  };

  // Loading points come from grade importer (only grades whose location is type 'loading')
  // Dedup by gradeId so multiple grades from the same location can all be added independently
  const unusedLoadingGrades = availableGrades.filter((grade) => {
    const matchedLocation = availableLocations.find(
      (loc) => loc.name === grade.locationName
    );
    return (
      matchedLocation?.locationType === 'loading' &&
      !loadingPoints.some((lp) => lp.gradeId === grade.gradeId)
    );
  });
  const unusedDumpingLocations = availableLocations.filter(
    (loc) =>
      loc.locationType === 'dumping' &&
      !dumpingPoints.some((dp) => dp.name === loc.name)
  );

  // Grade lookup helper for the table display
  const getCuGrade = (locationName: string): string => {
    const g = availableGrades.find((g) => g.locationName === locationName);
    const val = g?.qualities?.[primaryElement];
    return val != null ? String(val) : '—';
  };

  // ── Styling ───────────────────────────────────────────────────────────────
  const border = enableDarkTheme ? palette.cardBorderColor : palette.borderGrey;
  const sectionBg = palette.boxBackground;
  const inputBg = enableDarkTheme ? palette.toolBarBackground : '#f8fafc';
  const labelColor = enableDarkTheme
    ? 'rgba(148,163,184,0.8)'
    : 'rgba(0,0,0,0.45)';

  const sectionSx = {
    p: 2,
    bgcolor: sectionBg,
    border: `1px solid ${border}`,
    borderRadius: 2,
  };

  const sectionLabelSx = {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    color: labelColor,
    mb: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  };

  const inlineInputSx = {
    '& .MuiInputBase-root': {
      height: 34,
      fontSize: 13,
      borderRadius: '6px',
      bgcolor: inputBg,
    },
    '& fieldset': { borderColor: border, borderRadius: '6px' },
    '& .MuiInputBase-root:hover fieldset': {
      borderColor: palette.primary,
    },
  };

  const fieldSx = (width: number | string = 140) => ({
    width,
    ...inlineInputSx,
  });

  const addBtnSx = {
    mt: 1.5,
    width: '100%',
    borderStyle: 'dashed',
    borderColor: border,
    color: palette.primary,
    '&:hover': {
      borderStyle: 'dashed',
      borderColor: palette.primary,
      bgcolor: `${palette.primary}0D`,
    },
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <CustomMuiThemeProvider>
      <Box
        sx={{
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
          overflow: 'auto',
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            sx={{ fontWeight: 500, fontSize: '15px', color: 'text.primary' }}
          >
            {t('please_update_the_site_configuration')}
          </Typography>
          <CustomLoadingButton
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={
              isSaving ? <CircularProgress size={14} color="inherit" /> : null
            }
          >
            {isSaving ? t('site_config_saving') : t('site_config_save')}
          </CustomLoadingButton>
        </Box>

        {saveAttempted && Object.values(validationErrors).some(Boolean) && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {t('please_fix_validation_errors_before_saving')}
          </Alert>
        )}

        {isSaving && (
          <Alert
            severity="info"
            icon={<CircularProgress size={16} color="inherit" />}
            sx={{ borderRadius: 2 }}
          >
            {t('cdp_calculating_fleet_count')}
          </Alert>
        )}

        {!isSaving && minimalFleetError && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {minimalFleetError}
          </Alert>
        )}

        {/* ── 1. SITE PRODUCTION TARGET ── */}
        <Paper elevation={0} sx={sectionSx}>
          <Typography sx={sectionLabelSx}>
            <Public sx={{ fontSize: 14, color: palette.primary }} />
            {t('site_production_target')}
            {/* <Chip
              label="Customer Input"
              size="small"
              sx={{
                height: 18,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.5px',
                bgcolor: 'rgba(168,85,247,0.15)',
                color: '#c084fc',
                border: '1px solid rgba(168,85,247,0.4)',
              }}
            /> */}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mb: 2 }}
          >
            {t('primary_driver_of_the_entire_plan_algorithm_uses_this_to_com')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: `${palette.primary}0F`,
              border: `1px solid ${border}`,
              borderRadius: 1.5,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '8px',
                  bgcolor: `${palette.primary}26`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <GpsFixed sx={{ color: palette.primary, fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: labelColor,
                  }}
                >
                  {t('daily_production_target')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('daily_tonnage_target_driving_the_entire_plan')}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                type="number"
                onWheel={(e) => {
                  (e.target as HTMLInputElement).blur();
                }}
                value={productionTarget}
                onChange={(e) => {
                  setProductionTarget(sanitize(e.target.value));
                }}
                inputProps={{
                  min: 0,
                  style: {
                    fontSize: 24,
                    fontWeight: 700,
                    width: 110,
                    textAlign: 'right',
                    padding: '4px 8px',
                  },
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    bgcolor: 'transparent',
                    borderRadius: '6px',
                  },
                  '& fieldset': { borderColor: border },
                }}
                size="small"
              />
              <Typography
                sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}
              >
                {t('site_config_tons')}
              </Typography>
            </Box>
          </Box>
          {validationErrors.productionTarget && (
            <Typography color="error" sx={{ fontSize: 12, mt: 1 }}>
              {validationErrors.productionTarget}
            </Typography>
          )}
        </Paper>

        {/* ── 2. DUMPING POINTS ── */}
        <Paper elevation={0} sx={sectionSx}>
          <Typography sx={sectionLabelSx}>
            {t('dm_assign_dumping_points_title')}
            {validationErrors.dumpingPoints && (
              <Typography
                component="span"
                color="error"
                sx={{
                  fontSize: 11,
                  ml: 1,
                  textTransform: 'none',
                  fontWeight: 400,
                }}
              >
                {validationErrors.dumpingPoints}
              </Typography>
            )}
            {/* <Chip
              label="Enhanced"
              size="small"
              sx={{
                height: 18,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.5px',
                bgcolor: 'rgba(16,185,129,0.12)',
                color: '#10b981',
                border: '1px solid rgba(16,185,129,0.3)',
              }}
            /> */}
          </Typography>

          <TableContainer>
            <Table size="small" sx={{ '& th, & td': { borderColor: border } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: inputBg }}>
                  {[
                    '',
                    t('site_config_name'),
                    t('site_config_col_capacity_thr'),
                    t('site_config_col_target_blend'),
                    t('site_config_col_dumping_time'),
                    '',
                  ].map((h, i) => (
                    <TableCell
                      key={`dp-h-${i}`}
                      sx={{
                        color: labelColor,
                        fontWeight: 700,
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        py: 1.25,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dumpingPoints.map((dp) => (
                  <TableRow
                    key={dp.id}
                    sx={{ '&:last-child td': { border: 0 } }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={dp.isSelected}
                        onChange={() => {
                          updateDumpingPoint(
                            dp.id,
                            'isSelected',
                            !dp.isSelected
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell>{dp.name}</TableCell>
                    <TableCell>
                      <TextField
                        value={
                          rawInputs[`dp-${dp.id}-capacity`] !== undefined
                            ? rawInputs[`dp-${dp.id}-capacity`]
                            : String(dp.dumpingCapacity)
                        }
                        onChange={(e) => {
                          const s = sanitize(e.target.value);
                          setRawInputs((prev) => ({
                            ...prev,
                            [`dp-${dp.id}-capacity`]: s,
                          }));
                          updateDumpingPoint(
                            dp.id,
                            'dumpingCapacity',
                            s === '' ? 0 : Number(s)
                          );
                        }}
                        type="number"
                        onWheel={(e) => {
                          (e.target as HTMLInputElement).blur();
                        }}
                        inputProps={{ min: 0 }}
                        size="small"
                        error={!!validationErrors[`dp-${dp.id}-capacity`]}
                        helperText={
                          validationErrors[`dp-${dp.id}-capacity`] || undefined
                        }
                        InputLabelProps={{ sx: { fontSize: '13px' } }}
                        sx={fieldSx(130)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={dumpingTargetGradeStrings[dp.id] ?? ''}
                        onChange={(e) => {
                          setDumpingTargetGradeStrings((prev) => ({
                            ...prev,
                            [dp.id]: e.target.value,
                          }));
                        }}
                        size="small"
                        error={!!validationErrors[`dp-${dp.id}-targetBlend`]}
                        helperText={
                          validationErrors[`dp-${dp.id}-targetBlend`] ||
                          undefined
                        }
                        InputLabelProps={{ sx: { fontSize: '13px' } }}
                        sx={fieldSx(140)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={
                          rawInputs[`dp-${dp.id}-duration`] !== undefined
                            ? rawInputs[`dp-${dp.id}-duration`]
                            : String(dp.dumpingDurationMins)
                        }
                        onChange={(e) => {
                          const s = sanitize(e.target.value);
                          setRawInputs((prev) => ({
                            ...prev,
                            [`dp-${dp.id}-duration`]: s,
                          }));
                          updateDumpingPoint(
                            dp.id,
                            'dumpingDurationMins',
                            s === '' ? 0 : Number(s)
                          );
                        }}
                        type="number"
                        onWheel={(e) => {
                          (e.target as HTMLInputElement).blur();
                        }}
                        inputProps={{ min: 1 }}
                        error={!!validationErrors[`dp-${dp.id}-duration`]}
                        helperText={
                          validationErrors[`dp-${dp.id}-duration`] || undefined
                        }
                        size="small"
                        InputLabelProps={{ sx: { fontSize: '13px' } }}
                        sx={fieldSx(130)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleDeleteDumpingPoint(dp.id);
                        }}
                        sx={{
                          color: 'text.disabled',
                          '&:hover': { color: '#dc2626' },
                        }}
                      >
                        <DeleteOutlined fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Add Dumping Point — opens dropdown */}
          <CustomLoadingButton
            variant="outlined"
            sx={addBtnSx}
            disabled={
              isLoadingLocations ||
              (availableLocations.some(
                (loc) => loc.locationType === 'dumping'
              ) &&
                unusedDumpingLocations.length === 0)
            }
            onClick={(e) => {
              if (unusedDumpingLocations.length > 0) {
                setAddDumpingMenuAnchor(e.currentTarget);
              }
            }}
          >
            {isLoadingLocations ? (
              <CircularProgress size={14} sx={{ mr: 1 }} />
            ) : null}
            {t('site_config_add_dumping_point')}
          </CustomLoadingButton>

          <Menu
            anchorEl={addDumpingMenuAnchor}
            open={Boolean(addDumpingMenuAnchor)}
            onClose={() => {
              setAddDumpingMenuAnchor(null);
            }}
            PaperProps={{
              sx: {
                bgcolor: sectionBg,
                border: `1px solid ${border}`,
                borderRadius: '8px',
                minWidth: 200,
                mt: 0.5,
              },
            }}
          >
            {unusedDumpingLocations.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="caption" color="text.secondary">
                  {t('all_dumping_areas_already_added')}
                </Typography>
              </MenuItem>
            ) : (
              unusedDumpingLocations.map((loc) => (
                <MenuItem
                  key={loc.locationId}
                  onClick={() => {
                    handleAddDumpingFromMenu(loc);
                  }}
                  sx={{ gap: 1, py: 1 }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: 'text.primary',
                      }}
                    >
                      {loc.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {loc.minutes} {t('min_dump')}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Menu>
        </Paper>

        {/* ── 3. LOADING POINTS ── */}
        <Paper elevation={0} sx={sectionSx}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Typography sx={{ ...sectionLabelSx, mb: 0 }}>
              {t('dm_assign_loading_points_title')}
              {validationErrors.loadingPoints && (
                <Typography
                  component="span"
                  color="error"
                  sx={{
                    fontSize: 11,
                    ml: 1,
                    textTransform: 'none',
                    fontWeight: 400,
                  }}
                >
                  {validationErrors.loadingPoints}
                </Typography>
              )}
              {/* <Chip
                label="Shift Selection"
                size="small"
                sx={{
                  height: 18,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  bgcolor: 'rgba(59,130,246,0.12)',
                  color: '#60a5fa',
                  border: `1px solid rgba(59,130,246,0.3)`,
                }}
              /> */}
            </Typography>
            {/* <Chip
              label="Shift 1"
              size="small"
              sx={{
                bgcolor: 'rgba(59,130,246,0.15)',
                color: '#60a5fa',
                border: `1px solid rgba(59,130,246,0.4)`,
                fontWeight: 700,
                fontSize: 11,
              }}
            /> */}
          </Box>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}
          >
            {t('define_loading_points_and_select_which_ones_are_active_for_t')}
          </Typography>

          <TableContainer>
            <Table size="small" sx={{ '& th, & td': { borderColor: border } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: inputBg }}>
                  {[
                    '',
                    t('site_config_col_material_grade_class'),
                    t('site_config_name'),
                    t('site_config_col_grade_percent', {
                      element: primaryElement,
                    }),
                    t('site_config_capacity'),
                    t('site_config_load_time'),
                    '',
                  ].map((h, i) => (
                    <TableCell
                      key={`lp-h-${i}`}
                      sx={{
                        color: labelColor,
                        fontWeight: 700,
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        py: 1.25,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingPoints.map((lp) => (
                  <TableRow
                    key={lp.id}
                    sx={{ '&:last-child td': { border: 0 } }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={lp.isSelected}
                        onChange={() => {
                          updateLoadingPoint(
                            lp.id,
                            'isSelected',
                            !lp.isSelected
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: 13 }}>
                        {lp.materialGrade || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{lp.name}</TableCell>
                    {/* Grade % Cu — read-only from grade API */}
                    <TableCell>
                      {lp.materialGradeValue != null
                        ? lp.materialGradeValue
                        : getCuGrade(lp.name)}
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        onWheel={(e) => {
                          (e.target as HTMLInputElement).blur();
                        }}
                        value={
                          rawInputs[`lp-${lp.id}-capacity`] !== undefined
                            ? rawInputs[`lp-${lp.id}-capacity`]
                            : String(lp.loadingCapacity)
                        }
                        onChange={(e) => {
                          const s = sanitize(e.target.value);
                          setRawInputs((prev) => ({
                            ...prev,
                            [`lp-${lp.id}-capacity`]: s,
                          }));
                          updateLoadingPoint(
                            lp.id,
                            'loadingCapacity',
                            s === '' ? 0 : Number(s)
                          );
                        }}
                        size="small"
                        error={!!validationErrors[`lp-${lp.id}-capacity`]}
                        helperText={
                          validationErrors[`lp-${lp.id}-capacity`] || undefined
                        }
                        sx={{ ...inlineInputSx, width: 90 }}
                        inputProps={{
                          min: 0,
                          style: { fontSize: 13, textAlign: 'center' },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        onWheel={(e) => {
                          (e.target as HTMLInputElement).blur();
                        }}
                        value={
                          rawInputs[`lp-${lp.id}-duration`] !== undefined
                            ? rawInputs[`lp-${lp.id}-duration`]
                            : String(lp.loadingDurationMins)
                        }
                        onChange={(e) => {
                          const s = sanitize(e.target.value);
                          setRawInputs((prev) => ({
                            ...prev,
                            [`lp-${lp.id}-duration`]: s,
                          }));
                          updateLoadingPoint(
                            lp.id,
                            'loadingDurationMins',
                            s === '' ? 0 : Number(s)
                          );
                        }}
                        error={!!validationErrors[`lp-${lp.id}-duration`]}
                        size="small"
                        sx={{ ...inlineInputSx, width: 80 }}
                        inputProps={{
                          min: 1,
                          style: { fontSize: 13, textAlign: 'center' },
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleDeleteLoadingPoint(lp.id);
                        }}
                        sx={{
                          color: 'text.disabled',
                          '&:hover': { color: '#dc2626' },
                        }}
                      >
                        <DeleteOutlined fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Add Loading Point — opens dropdown */}
          <CustomLoadingButton
            variant="outlined"
            sx={addBtnSx}
            disabled={
              isLoadingLocations ||
              (availableGrades.length > 0 && unusedLoadingGrades.length === 0)
            }
            onClick={(e) => {
              if (unusedLoadingGrades.length > 0) {
                setAddLoadingMenuAnchor(e.currentTarget);
              }
            }}
          >
            {isLoadingLocations ? (
              <CircularProgress size={14} sx={{ mr: 1 }} />
            ) : null}
            {t('site_config_add_loading_point')}
          </CustomLoadingButton>

          <Menu
            anchorEl={addLoadingMenuAnchor}
            open={Boolean(addLoadingMenuAnchor)}
            onClose={() => {
              setAddLoadingMenuAnchor(null);
            }}
            PaperProps={{
              sx: {
                bgcolor: sectionBg,
                border: `1px solid ${border}`,
                borderRadius: '8px',
                minWidth: 220,
                mt: 0.5,
              },
            }}
          >
            {unusedLoadingGrades.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="caption" color="text.secondary">
                  {t('all_loading_areas_already_added')}
                </Typography>
              </MenuItem>
            ) : (
              unusedLoadingGrades.map((grade) => (
                <MenuItem
                  key={grade.gradeId}
                  onClick={() => {
                    handleAddLoadingFromMenu(grade);
                  }}
                  sx={{ gap: 1, py: 1 }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: 'text.primary',
                      }}
                    >
                      {grade.locationName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {grade.name}
                      {grade.qualities?.CU != null
                        ? ` · CU ${grade.qualities.CU}%`
                        : ''}
                      {grade.inventory > 0 ? ` · ${grade.inventory}t` : ''}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Menu>
        </Paper>

        {/* ── 4. TOLERANCES & CONSTRAINTS ── */}
        <Paper elevation={0} sx={sectionSx}>
          <Typography sx={sectionLabelSx}>
            <Tune sx={{ fontSize: 14, color: palette.primary }} />
            {t('site_config_tolerances_constraints')}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mb: 2 }}
          >
            {t('operational_buffers_applied_during_plan_generation')}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box
              sx={{
                p: 1.75,
                bgcolor: inputBg,
                border: `1px solid ${border}`,
                borderRadius: 1.5,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AccessTime sx={{ color: palette.primary, fontSize: 14 }} />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: labelColor,
                  }}
                >
                  {t('idling')}
                </Typography>
              </Box>
              <Box display="flex" alignItems="baseline" gap={0.75}>
                <TextField
                  type="number"
                  onWheel={(e) => {
                    (e.target as HTMLInputElement).blur();
                  }}
                  value={excessIdling}
                  onChange={(e) => {
                    setExcessIdling(sanitize(e.target.value));
                  }}
                  inputProps={{
                    min: 0,
                    max: 100,
                    style: {
                      fontSize: 22,
                      fontWeight: 700,
                      width: 70,
                      padding: '4px 6px',
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { bgcolor: 'transparent' },
                    '& fieldset': { borderColor: border },
                  }}
                  size="small"
                />
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  %
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}
              >
                {t('expected_equipment_idle_time')}
              </Typography>
              {validationErrors.excessIdling && (
                <Typography color="error" sx={{ fontSize: 11, mt: 0.5 }}>
                  {validationErrors.excessIdling}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                p: 1.75,
                bgcolor: inputBg,
                border: `1px solid ${border}`,
                borderRadius: 1.5,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AccessTime sx={{ color: palette.primary, fontSize: 14 }} />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: labelColor,
                  }}
                >
                  {t('site_config_lunch_tolerance')}
                </Typography>
              </Box>
              <Box display="flex" alignItems="baseline" gap={0.75}>
                <TextField
                  type="number"
                  onWheel={(e) => {
                    (e.target as HTMLInputElement).blur();
                  }}
                  value={lunchTolerance}
                  onChange={(e) => {
                    setLunchTolerance(sanitize(e.target.value));
                  }}
                  inputProps={{
                    min: 0,
                    style: {
                      fontSize: 22,
                      fontWeight: 700,
                      width: 70,
                      padding: '4px 6px',
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { bgcolor: 'transparent' },
                    '& fieldset': { borderColor: border },
                  }}
                  size="small"
                />
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {t('site_config_mins')}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}
              >
                {t('allowed_lunch_break_window')}
              </Typography>
              {validationErrors.lunchTolerance && (
                <Typography color="error" sx={{ fontSize: 11, mt: 0.5 }}>
                  {validationErrors.lunchTolerance}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </CustomMuiThemeProvider>
  );
};
